import { API_URL } from './config';
import { tokenStore } from './api';

const UPLOADS_PREFIX = `${API_URL}/uploads/`;

/**
 * Appends the media pass to one upload URL.
 *
 * `/uploads` is authenticated, and the things that read it — `<img>`,
 * `<video>`, `<iframe>`, a `download` link — cannot send a header, so the
 * credential has to ride in the URL.
 */
export function mediaUrl(url: string): string {
  if (!url.startsWith(UPLOADS_PREFIX)) return url;
  const pass = tokenStore.media;
  if (!pass || /[?&]m=/.test(url)) return url;
  return `${url}${url.includes('?') ? '&' : '?'}m=${encodeURIComponent(pass)}`;
}

/**
 * Stamps every upload URL in a payload on its way into the app.
 *
 * Done here, at the one door all server data comes through, rather than at the
 * hundred-odd places that render a picture: an avatar, a chat attachment, a
 * story, a custom emoji and a whiteboard image are all just strings by the
 * time they reach a component, and any one of them left unstamped would be a
 * broken image nobody notices until it is in front of a customer.
 *
 * Returns the same object it was given — the rewrite is in place, so a caller
 * holding a reference to a nested node sees the stamped URL too.
 */
export function stampMedia<T>(payload: T): T {
  walk(payload, 0);
  return payload;
}

/** Guards against a cyclic payload, which socket.io in particular can hand us. */
const MAX_DEPTH = 12;

function walk(node: unknown, depth: number): void {
  if (depth > MAX_DEPTH || node === null || typeof node !== 'object') return;

  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i += 1) {
      const item = node[i];
      if (typeof item === 'string') node[i] = mediaUrl(item);
      else walk(item, depth + 1);
    }
    return;
  }

  const record = node as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    const value = record[key];
    if (typeof value === 'string') record[key] = mediaUrl(value);
    else walk(value, depth + 1);
  }
}

/**
 * Stamps the payload an emit's acknowledgement hands back.
 *
 * `onAny` catches broadcasts only — socket.io invokes an ack callback
 * directly, so it never passes through that hook. Anything that arrives as
 * the answer to an emit therefore skipped the stamp: the space join ack
 * carries `avatarUrl` for everybody already in the room, and those photos
 * came back as raw `/uploads` URLs and 401'd, while somebody who walked in a
 * moment later — a broadcast — wore theirs. Doc and sheet subscriptions
 * return their content the same way.
 *
 * Patching the instance's `emit` is safe: incoming events are dispatched
 * through `super.emit`, which resolves on the prototype and does not see this.
 */
export function stampAcks(socket: unknown): void {
  const target = socket as { emit: (...args: unknown[]) => unknown };
  const emit = target.emit.bind(target);
  target.emit = (...args: unknown[]) => {
    const last = args[args.length - 1];
    if (typeof last === 'function') {
      const ack = last as (...ackArgs: unknown[]) => void;
      args[args.length - 1] = (...ackArgs: unknown[]) => ack(...stampMedia(ackArgs));
    }
    return emit(...args);
  };
}
